import clsx from 'clsx';
import { fmtTime } from '../../shared/dates';
import { DayLabels, WeekdayOrder, type DayOfWeek } from '@census/shared';

export interface Block {
  day: DayOfWeek;
  startMin: number;
  endMin: number;
  label: string;
  sub?: string;
  kind: 'GROUP' | 'INDIVIDUAL';
  status?: string;
}

interface Props {
  blocks: Block[];
  startHour?: number;
  endHour?: number;
  rowMinutes?: number;
  days?: DayOfWeek[];
}

export function ScheduleGrid({
  blocks,
  startHour = 8,
  endHour = 20,
  rowMinutes = 30,
  days = WeekdayOrder,
}: Props) {
  const totalRows = ((endHour - startHour) * 60) / rowMinutes;
  const rows = Array.from({ length: totalRows + 1 }, (_, i) => startHour * 60 + i * rowMinutes);

  return (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[640px] rounded-md border border-slate-200 bg-white"
        style={{
          gridTemplateColumns: `64px repeat(${days.length}, minmax(120px, 1fr))`,
        }}
      >
        <div className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-500"></div>
        {days.map((d) => (
          <div
            key={d}
            className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-center text-xs font-semibold text-slate-600"
          >
            {DayLabels[d]}
          </div>
        ))}

        {rows.map((minute, idx) => (
          <Row key={minute} minute={minute} idx={idx} days={days} blocks={blocks} rowMinutes={rowMinutes} />
        ))}
      </div>
    </div>
  );
}

function Row({
  minute,
  idx,
  days,
  blocks,
  rowMinutes,
}: {
  minute: number;
  idx: number;
  days: DayOfWeek[];
  blocks: Block[];
  rowMinutes: number;
}) {
  return (
    <>
      <div
        className={clsx(
          'border-b border-slate-100 px-2 py-1 text-right text-[10px] text-slate-400',
          idx % 2 === 0 ? 'bg-slate-50' : 'bg-white',
        )}
      >
        {fmtTime(minute)}
      </div>
      {days.map((d) => {
        // Range-based: a block "starts" in this row if startMin in [minute, minute+rowMinutes).
        // Handles non-aligned starts (e.g. 13:15 service in a 30-min grid).
        // Used to be `find` — that hid parallel-starting bookings (e.g., two
        // overlapping individuals in the same row). `filter` + stack-index
        // mirrors the ResourceCalendarView fix so the print + intake-preview
        // grids stay accurate.
        const startsHere = blocks.filter(
          (b) => b.day === d && b.startMin >= minute && b.startMin < minute + rowMinutes,
        );
        return (
          <div
            key={d}
            className={clsx(
              'relative h-7 border-b border-slate-100',
              idx % 2 === 0 ? 'bg-slate-50/40' : 'bg-white',
            )}
          >
            {startsHere.map((b, i) => (
              <BlockBadge
                key={`${b.day}|${b.startMin}|${b.label}|${i}`}
                block={b}
                rowMinutes={rowMinutes}
                stackIndex={i}
                stackCount={startsHere.length}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}

function BlockBadge({
  block,
  rowMinutes,
  stackIndex = 0,
  stackCount = 1,
}: {
  block: Block;
  rowMinutes: number;
  stackIndex?: number;
  stackCount?: number;
}) {
  const rowsSpan = Math.max(1, (block.endMin - block.startMin) / rowMinutes);
  const height = `calc(${rowsSpan * 28}px - 4px)`;
  const colorClass =
    block.kind === 'GROUP'
      ? 'border-brand-300 bg-brand-50 text-brand-900'
      : 'border-emerald-300 bg-emerald-50 text-emerald-900';

  const statusOverride =
    block.status === 'ATTENDED' || block.status === 'LATE'
      ? 'border-emerald-400 bg-emerald-100'
      : block.status === 'NO_SHOW'
        ? 'border-rose-400 bg-rose-100 text-rose-900'
        : block.status?.startsWith('CANCELLED')
          ? 'border-slate-300 bg-slate-100 text-slate-500 line-through'
          : '';

  // Split the day column evenly when multiple blocks start in the same row.
  const widthPct = 100 / Math.max(1, stackCount);
  const leftPct = stackIndex * widthPct;
  return (
    <div
      className={clsx(
        'absolute z-[1] overflow-hidden rounded border px-2 py-1 text-xs shadow-sm',
        colorClass,
        statusOverride,
      )}
      style={{
        height,
        top: 2,
        left: `calc(${leftPct}% + 4px)`,
        width: `calc(${widthPct}% - 8px)`,
      }}
    >
      <div className="truncate font-semibold">{block.label}</div>
      {block.sub && <div className="truncate text-[10px] opacity-75">{block.sub}</div>}
    </div>
  );
}
